import * as React from "react";
import { ZoomIn, ZoomOut } from "lucide-react";

import { RangeSlider } from "./ui/range-slider.js";
import { clampOffset, coverScale, PHOTO_RULES, type CropState } from "../lib/profilePhoto.js";

// On-screen size of the square crop area, in px. renderCrop maps it to 512x512.
export const CROP_VIEW_PX = 240;

const KEY_STEP = 8;

interface ProfilePhotoCropperProps {
  src: string;
  width: number;
  height: number;
  crop: CropState;
  onCropChange: (crop: CropState) => void;
}

/** Drag to position, slider / wheel / arrow keys to adjust. The circle is what gets saved. */
export function ProfilePhotoCropper({ src, width, height, crop, onCropChange }: ProfilePhotoCropperProps) {
  const drag = React.useRef<{ px: number; py: number; x: number; y: number } | null>(null);
  const set = (next: CropState) => onCropChange(clampOffset(next, width, height, CROP_VIEW_PX));
  const s = coverScale(width, height, CROP_VIEW_PX) * crop.zoom;

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { px: e.clientX, py: e.clientY, x: crop.x, y: crop.y };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    set({ zoom: crop.zoom, x: d.x + e.clientX - d.px, y: d.y + e.clientY - d.py });
  };
  const endDrag = () => { drag.current = null; };

  const zoomTo = (zoom: number) => {
    const z = Math.min(PHOTO_RULES.maxZoom, Math.max(1, zoom));
    // Keep the point under the crop centre fixed while zooming.
    const k = z / crop.zoom;
    set({ zoom: z, x: crop.x * k, y: crop.y * k });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [KEY_STEP, 0], ArrowRight: [-KEY_STEP, 0], ArrowUp: [0, KEY_STEP], ArrowDown: [0, -KEY_STEP],
    };
    const m = moves[e.key];
    if (m) {
      e.preventDefault();
      set({ zoom: crop.zoom, x: crop.x + m[0], y: crop.y + m[1] });
    } else if (e.key === "+" || e.key === "=") {
      zoomTo(crop.zoom + 0.1);
    } else if (e.key === "-") {
      zoomTo(crop.zoom - 0.1);
    }
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        role="application"
        aria-label="Photo position. Drag or use the arrow keys to move, plus and minus to zoom."
        tabIndex={0}
        className="photo-crop relative size-60 cursor-grab touch-none overflow-hidden rounded-xl bg-surface-900 outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-0 active:cursor-grabbing"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onWheel={e => zoomTo(crop.zoom - e.deltaY * 0.002)}
        onKeyDown={onKeyDown}
      >
        <img
          src={src}
          alt=""
          draggable={false}
          className="photo-crop-img"
          // Pointer-drag position and zoom are recomputed on every move, so they can't be static classes.
          style={{
            "--crop-w": `${width * s}px`,
            "--crop-h": `${height * s}px`,
            "--crop-x": `${crop.x}px`,
            "--crop-y": `${crop.y}px`,
          } as React.CSSProperties}
        />
        <div className="photo-crop-mask pointer-events-none absolute inset-0" aria-hidden="true" />
      </div>
      <div className="flex w-60 items-center gap-2 text-surface-500">
        <ZoomOut size={15} aria-hidden="true" />
        <RangeSlider
          aria-label="Zoom"
          min={1}
          max={PHOTO_RULES.maxZoom}
          step={0.01}
          value={crop.zoom}
          onValueChange={zoomTo}
        />
        <ZoomIn size={15} aria-hidden="true" />
      </div>
    </div>
  );
}
